import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const PromptGenerator = () => {
  const [config, setConfig] = useState({
    buildPlatform: 'Greta',
    repository: 'GitHub',
    deploymentHost: 'Cloudflare Pages',
    backend: 'No Code Backend',
    storage: 'Dropbox',
    transferFromNetlify: false
  });

  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [copied, setCopied] = useState(false);

  const buildPlatforms = ['Greta', 'Lovable', 'Base44'];
  const deploymentHosts = ['Cloudflare Pages', 'Netlify'];
  const backends = ['No Code Backend', 'Supabase'];
  const storages = ['Dropbox', 'Google Drive', 'Netlify'];

  const generatePrompt = () => {
    let prompt = '';

    // Base framework setup
    prompt += `Greta, prepare this project for ${config.buildPlatform} build with ${config.repository} repository, deploying to ${config.deploymentHost}, using ${config.backend} for backend functionality, and ${config.storage} for storage.\n\n`;

    // Cloudflare Pages specific instructions
    if (config.deploymentHost === 'Cloudflare Pages' && !config.transferFromNetlify) {
      prompt += `1) Files & structure\n`;
      prompt += `- Create a root file \`_redirects\` with exactly:\n`;
      prompt += `  /*    /index.html    200\n`;
      prompt += `- Create a root file \`_headers\` with exactly:\n`;
      prompt += `  /*\n`;
      prompt += `    X-Frame-Options: DENY\n`;
      prompt += `    X-Content-Type-Options: nosniff\n`;
      prompt += `    Referrer-Policy: strict-origin-when-cross-origin\n`;
      prompt += `    Permissions-Policy: geolocation=(), microphone=(), camera=()\n`;
      prompt += `    Strict-Transport-Security: max-age=31536000; includeSubDomains; preload\n`;
      prompt += `- Ensure the build output directory is \`dist/\` (Vite default). If the project uses Vite:\n`;
      prompt += `  - Confirm \`vite.config.ts/js\` sets \`base: '/\'.\n`;
      prompt += `- Add a minimal Cloudflare Pages Function:\n`;
      prompt += `  - Create folder \`/functions/api\`.\n`;
      prompt += `  - Add \`/functions/api/ping.ts\` with:\n`;
      prompt += `    export const onRequestGet: PagesFunction = async () => {\n`;
      prompt += `      return new Response(JSON.stringify({ ok: true, time: new Date().toISOString() }), {\n`;
      prompt += `        headers: { 'content-type': 'application/json' }\n`;
      prompt += `      });\n`;
      prompt += `    };\n\n`;

      prompt += `2) Sample form endpoint (optional, kept tiny)\n`;
      prompt += `- Add \`/functions/api/form.ts\` that:\n`;
      prompt += `  - Accepts POST formData with fields name, email, message.\n`;
      prompt += `  - Returns JSON { ok: true } on success and { ok: false, error } on failure.\n`;
      prompt += `  - Code:\n`;
      prompt += `    export const onRequestPost: PagesFunction = async ({ request }) => {\n`;
      prompt += `      try {\n`;
      prompt += `        const form = await request.formData();\n`;
      prompt += `        const name = String(form.get('name') ?? '');\n`;
      prompt += `        const email = String(form.get('email') ?? '');\n`;
      prompt += `        const message = String(form.get('message') ?? '');\n`;
      prompt += `        if (!email || !message) {\n`;
      prompt += `          return new Response(JSON.stringify({ ok: false, error: 'Missing fields' }), { status: 400, headers: { 'content-type': 'application/json' }});\n`;
      prompt += `        }\n`;
      prompt += `        // TODO: integrate email/webhook/CRM here if needed\n`;
      prompt += `        return new Response(JSON.stringify({ ok: true }), { headers: { 'content-type': 'application/json' }});\n`;
      prompt += `      } catch (e) {\n`;
      prompt += `        return new Response(JSON.stringify({ ok: false, error: 'Bad request' }), { status: 400, headers: { 'content-type': 'application/json' }});\n`;
      prompt += `      }\n`;
      prompt += `    };\n\n`;

      prompt += `3) Frontend wiring\n`;
      prompt += `- Add a small utility at \`src/lib/env.ts\`:\n`;
      prompt += `  export const env = {\n`;
      prompt += `    stage: import.meta.env.VITE_STAGE ?? (location.hostname.includes('pages.dev') ? 'preview' : 'prod'),\n`;
      prompt += `    apiBase: '/api'\n`;
      prompt += `  };\n`;
      prompt += `- Add a demo usage in the home page or a new page \`src/pages/Diagnostics.jsx\`:\n`;
      prompt += `  - Button that fetches \`/api/ping\` and shows the JSON.\n`;
      prompt += `  - If there is an existing router, add a route \`/diagnostics\`.\n\n`;

      prompt += `4) NPM scripts & Node version\n`;
      prompt += `- Ensure package.json includes:\n`;
      prompt += `  - "build": "vite build"\n`;
      prompt += `  - "preview": "vite preview --port 4173"\n`;
      prompt += `- If missing, add an \`.nvmrc\` file with the current LTS (e.g., \`20\`).\n\n`;

      prompt += `5) Documentation\n`;
      prompt += `- Update README.md with a new section "Deploy to Cloudflare Pages" containing:\n`;
      prompt += `  - Steps:\n`;
      prompt += `    1. In Cloudflare dashboard → Pages → Create Project → Connect to Git (select this repo).\n`;
      prompt += `    2. Framework preset: Vite (or None).\n`;
      prompt += `    3. Build command: \`npm run build\`\n`;
      prompt += `    4. Output directory: \`dist\`\n`;
      prompt += `    5. (Optional) Add env vars under Settings → Environment (prefix client vars with \`VITE_\`).\n`;
      prompt += `    6. Deploy. Your site appears at https://<project>.pages.dev\n`;
      prompt += `  - Notes:\n`;
      prompt += `    - For SPA routing, \`_redirects\` handles deep links.\n`;
      prompt += `    - Security headers are set via \`_headers\`.\n`;
      prompt += `    - Test endpoints: GET \`/api/ping\`, POST \`/api/form\`.\n`;
      prompt += `    - To add more APIs, create files under \`/functions/api/*.ts\` using the PagesFunction signature.\n\n`;

      prompt += `6) Optional: Turnstile integration scaffold (commented)\n`;
      prompt += `- In README, include a commented quickstart showing how to add a Turnstile widget and verify token server-side in \`/functions/api/form.ts\`.\n\n`;

      prompt += `7) Git hygiene\n`;
      prompt += `- If not present, add \`.gitignore\` entries for:\n`;
      prompt += `  /dist\n`;
      prompt += `  .DS_Store\n`;
      prompt += `  node_modules\n\n`;

      prompt += `8) Commit\n`;
      prompt += `- Commit all changes with:\n`;
      prompt += `  chore(cf): Cloudflare Pages bootstrap (_redirects, _headers, functions, docs)\n\n`;
    }

    // Netlify specific instructions
    if (config.deploymentHost === 'Netlify') {
      prompt += `1) Files & structure\n`;
      prompt += `- Create a root file \`netlify.toml\` with:\n`;
      prompt += `  [build]\n`;
      prompt += `    publish = "dist"\n`;
      prompt += `    command = "npm run build"\n\n`;
      prompt += `  [build.environment]\n`;
      prompt += `    NODE_VERSION = "20"\n\n`;
      prompt += `  [[redirects]]\n`;
      prompt += `    from = "/*"\n`;
      prompt += `    to = "/index.html"\n`;
      prompt += `    status = 200\n\n`;
      prompt += `2) Netlify Functions\n`;
      prompt += `- Create \`netlify/functions\` folder\n`;
      prompt += `- Add \`netlify/functions/ping.js\` with:\n`;
      prompt += `  exports.handler = async () => {\n`;
      prompt += `    return {\n`;
      prompt += `      statusCode: 200,\n`;
      prompt += `      body: JSON.stringify({ ok: true, time: new Date().toISOString() })\n`;
      prompt += `    };\n`;
      prompt += `  };\n\n`;
      prompt += `3) Frontend wiring\n`;
      prompt += `- Add utility at \`src/lib/env.js\`:\n`;
      prompt += `  export const env = {\n`;
      prompt += `    stage: import.meta.env.VITE_STAGE ?? 'prod',\n`;
      prompt += `    apiBase: '/.netlify/functions'\n`;
      prompt += `  };\n\n`;
      prompt += `4) Commit with:\n`;
      prompt += `  chore(netlify): Netlify bootstrap (netlify.toml, functions)\n\n`;
    }

    // Lovable specific instructions
    if (config.buildPlatform === 'Lovable') {
      prompt += `1) Lovable Configuration\n`;
      prompt += `- Ensure project structure follows Lovable conventions\n`;
      prompt += `- Add \`lovable.config.json\` with basic settings\n`;
      prompt += `- Optimize for Lovable's deployment pipeline\n\n`;
    }

    // Base44 specific instructions
    if (config.buildPlatform === 'Base44') {
      prompt += `1) Base44 Configuration\n`;
      prompt += `- Configure for Base44 build system\n`;
      prompt += `- Add \`base44.config.js\` if needed\n`;
      prompt += `- Follow Base44 deployment guidelines\n\n`;
    }

    // Backend specific instructions
    if (config.backend === 'Supabase') {
      prompt += `9) Supabase Integration\n`;
      prompt += `- Install @supabase/supabase-js\n`;
      prompt += `- Create \`src/supabase.js\` with client configuration\n`;
      prompt += `- Set up authentication schema\n`;
      prompt += `- Configure Row Level Security (RLS)\n`;
      prompt += `- Add environment variables for Supabase URL and anon key\n\n`;
    }

    // Storage specific instructions
    if (config.storage === 'Dropbox') {
      prompt += `10) Dropbox Integration\n`;
      prompt += `- Install Dropbox SDK\n`;
      prompt += `- Set up OAuth credentials\n`;
      prompt += `- Configure file upload/download handlers\n\n`;
    } else if (config.storage === 'Google Drive') {
      prompt += `10) Google Drive Integration\n`;
      prompt += `- Install Google Drive API SDK\n`;
      prompt += `- Set up service account credentials\n`;
      prompt += `- Configure file management endpoints\n\n`;
    } else if (config.storage === 'Netlify') {
      prompt += `10) Netlify Storage Integration\n`;
      prompt += `- Use Netlify Forms for data collection\n`;
      prompt += `- Configure Netlify Large Media for file storage\n\n`;
    }

    // Transfer from Netlify to Cloudflare
    if (config.transferFromNetlify) {
      prompt += `Greta, replace Netlify-specific pieces with Cloudflare equivalents:\n\n`;
      prompt += `- Remove reliance on netlify.toml redirects; rely on \`_redirects\` and \`_headers\`.\n`;
      prompt += `- If there are Netlify Functions under \`netlify/functions\`, port the minimal ones to Cloudflare Pages Functions under \`/functions/api\` using the PagesFunction handler pattern.\n`;
      prompt += `- Replace any \`/.netlify/functions/...\` client calls with \`/api/...\`.\n`;
      prompt += `- If the app used Netlify Forms, update contact forms to POST to \`/api/form\` and document Turnstile as an optional anti-bot measure in README.\n`;
      prompt += `- Commit as "refactor(cf): migrate Netlify-isms to Cloudflare Pages functions".\n\n`;
    }

    setGeneratedPrompt(prompt);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4">Greta Prompt Generator</h1>
        <p className="text-gray-600">Configure your project setup and generate the perfect initial prompt</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Configuration Panel */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-6">Configuration</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Build Platform</label>
              <select 
                value={config.buildPlatform}
                onChange={(e) => setConfig({...config, buildPlatform: e.target.value})}
                className="w-full border rounded px-3 py-2"
              >
                {buildPlatforms.map(platform => (
                  <option key={platform} value={platform}>{platform}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Repository Storage</label>
              <div className="w-full border rounded px-3 py-2 bg-gray-50">
                GitHub (Only Option)
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Deployment Host</label>
              <select 
                value={config.deploymentHost}
                onChange={(e) => setConfig({...config, deploymentHost: e.target.value})}
                className="w-full border rounded px-3 py-2"
              >
                {deploymentHosts.map(host => (
                  <option key={host} value={host}>{host}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Backend Platform</label>
              <select 
                value={config.backend}
                onChange={(e) => setConfig({...config, backend: e.target.value})}
                className="w-full border rounded px-3 py-2"
              >
                {backends.map(backend => (
                  <option key={backend} value={backend}>{backend}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Storage Option</label>
              <select 
                value={config.storage}
                onChange={(e) => setConfig({...config, storage: e.target.value})}
                className="w-full border rounded px-3 py-2"
              >
                {storages.map(storage => (
                  <option key={storage} value={storage}>{storage}</option>
                ))}
              </select>
            </div>

            {config.deploymentHost === 'Cloudflare Pages' && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="transferFromNetlify"
                  checked={config.transferFromNetlify}
                  onChange={(e) => setConfig({...config, transferFromNetlify: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="transferFromNetlify" className="text-sm">
                  Transfer from Netlify to Cloudflare Pages
                </label>
              </div>
            )}

            <button
              onClick={generatePrompt}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded font-medium transition-colors"
            >
              Generate Prompt
            </button>
          </div>
        </div>

        {/* Generated Prompt Panel */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Generated Prompt</h2>
            {generatedPrompt && (
              <button
                onClick={copyToClipboard}
                className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition-colors"
              >
                <SafeIcon icon={FiIcons.FiCopy} />
                <span>{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            )}
          </div>
          
          {generatedPrompt ? (
            <div className="bg-gray-50 rounded p-4 h-96 overflow-auto">
              <pre className="text-sm whitespace-pre-wrap">{generatedPrompt}</pre>
            </div>
          ) : (
            <div className="bg-gray-50 rounded p-4 h-96 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <SafeIcon icon={FiIcons.FiFileText} className="text-4xl mb-2 mx-auto" />
                <p>Configure your settings and click "Generate Prompt" to create your initial build prompt</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Summary */}
      {generatedPrompt && (
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4">
          <h3 className="font-semibold mb-2">Configuration Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            <div><strong>Build Platform:</strong> {config.buildPlatform}</div>
            <div><strong>Repository:</strong> {config.repository}</div>
            <div><strong>Deployment:</strong> {config.deploymentHost}</div>
            <div><strong>Backend:</strong> {config.backend}</div>
            <div><strong>Storage:</strong> {config.storage}</div>
            {config.transferFromNetlify && (
              <div><strong>Transfer:</strong> Netlify → Cloudflare</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptGenerator;